import React, { useCallback, useState } from 'react'
import { camelCase, groupBy } from 'lodash'
import { ClientInteractions } from 'types'
import {
    getInteractionsFromClientStatus,
    InteractionButton
} from 'components/interactions/constants'
import { Button } from 'components/button/Button'
import { useMobilePayment } from 'hooks'
import { ClientStatus, InteractionPayloadType } from 'storeTypes'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/modal'

import './interactions.scss'
import { InteractionFollowup } from './InteractionFollowup'

interface InteractionsProps {
    clientStatus: ClientStatus
    clientInteraction?: InteractionPayloadType
    interactionHandler: (interaction: { name: string; outcome: string }) => void
    canRecordInteractions: boolean
    canMakeCardPayment?: boolean
}

export const Interactions = ({
    clientStatus,
    clientInteraction,
    interactionHandler,
    canRecordInteractions,
    canMakeCardPayment = false
}: InteractionsProps) => {
    const { t } = useTranslation()
    const [showModal, setShowModal] = useState(false)
    const [followupInteraction, setFollowupInteraction] = useState<{
        interactionName: string
        interactionOutcome: string
    } | null>(null)

    const { isMobilePaymentEnabled } = useMobilePayment()

    const toggleModal = () => {
        setShowModal(!showModal)
    }

    const filterInteractions = useCallback(
        (interactions: InteractionButton[] = []) => {
            interactions = interactions.filter((interaction) => {
                const filterCardPayment =
                    interaction.name === ClientInteractions.CARD_PAYMENT &&
                    !canMakeCardPayment

                const filterMobileMoneyPayment =
                    interaction.name ===
                        ClientInteractions.MOBILE_MONEY_PAYMENT &&
                    !isMobilePaymentEnabled

                return !(filterCardPayment || filterMobileMoneyPayment)
            })

            return interactions
        },
        [clientInteraction, canMakeCardPayment, isMobilePaymentEnabled]
    )

    const interactionButtonHandler = (interaction: InteractionButton) => {
        const isFollowupInteraction =
            interaction.name === ClientInteractions.REQUEST_FOLLOW_UP

        if (isFollowupInteraction) {
            setFollowupInteraction({
                interactionName: interaction.name,
                interactionOutcome: interaction.outcome
            })
        } else {
            interactionHandler({
                name: interaction.name,
                outcome: interaction.outcome
            })
        }

        setShowModal(false)
    }

    const buildInteractionButton = useCallback(
        (interaction: InteractionButton) => {
            const ariaLabel = `interaction-${interaction.name.replace(
                /\s+/g,
                '-'
            )}-btn`

            return (
                <span
                    className="interaction-button-container"
                    key={interaction.name}
                >
                    <button
                        type="button"
                        aria-label={ariaLabel}
                        className={ariaLabel}
                        onClick={() => interactionButtonHandler(interaction)}
                        disabled={!canRecordInteractions}
                    >
                        <interaction.icon />
                    </button>
                    {interaction.displayLabel && (
                        <span className="interaction-button-label">
                            {t('clientDetails.' + camelCase(interaction.name))}
                        </span>
                    )}
                </span>
            )
        },
        [canRecordInteractions, interactionHandler, t]
    )

    const buildInteractionList = () => {
        const interactions = getInteractionsFromClientStatus(clientStatus)
        const filteredInteractions = filterInteractions(interactions)
        const bySection = groupBy(filteredInteractions, 'section')

        return Object.entries(bySection).map(
            ([sectionName, interactionsInSection]) => {
                const sectionTitle = t('interactions.' + sectionName)
                const sectionButtons = interactionsInSection.map(
                    (interaction) => buildInteractionButton(interaction)
                )

                return (
                    <div key={sectionName} className="interactions-section">
                        <h3 className="interactions-section-title">
                            {sectionTitle}
                        </h3>
                        <div className="interactions-section-buttons">
                            {sectionButtons}
                        </div>
                    </div>
                )
            }
        )
    }

    return (
        <div className="interactions-container">
            <Button
                style="primary"
                iconPos="right"
                size="tall"
                onClick={toggleModal}
                ariaLabel="interactions-button"
            >
                {t('clientDetails.interactions')}
            </Button>
            <Modal
                showModal={showModal || !!followupInteraction}
                position="bottom"
                closeClickHandler={() => {
                    setShowModal(false)
                    setFollowupInteraction(null)
                }}
                showCloseIconBtn
                aria-label="interactions-modal"
            >
                <div className="top-close-bar"></div>
                {followupInteraction ? (
                    <InteractionFollowup
                        interactionHandler={interactionHandler}
                        followupInteraction={followupInteraction}
                        setFollowupInteraction={setFollowupInteraction}
                        t={t}
                    />
                ) : (
                    <div className="interactions-buttons-container">
                        {buildInteractionList()}
                    </div>
                )}
            </Modal>
        </div>
    )
}
